import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import client from "../../src/api/client";
import "../styles/admin-base.css";
import "../styles/notifications.css";

const DEFAULT_STATE = {
  badgeCount: 0,
  unreadCount: 0,
  items: [],
};

const normalizeNotificationsState = (value) => {
  const next = value && typeof value === "object" ? value : {};
  return {
    badgeCount: Number(next.badgeCount ?? 0),
    unreadCount: Number(next.unreadCount ?? 0),
    items: Array.isArray(next.items) ? next.items : [],
  };
};

const parseNotificationDate = (item) => {
  const raw = item?.createdAt ?? item?.created_at;
  if (!raw) {
    return null;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const groupNotificationsByDate = (items) => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const grouped = {
    new: [],
    today: [],
    earlier: [],
  };

  items.forEach((item) => {
    const createdAt = parseNotificationDate(item);
    if (!createdAt) {
      grouped.earlier.push(item);
      return;
    }

    const ageMs = now.getTime() - createdAt.getTime();
    if (ageMs <= 60 * 60 * 1000) {
      grouped.new.push(item);
      return;
    }

    if (createdAt >= startOfToday) {
      grouped.today.push(item);
      return;
    }

    grouped.earlier.push(item);
  });

  return [
    { key: "new", label: "New", items: grouped.new },
    { key: "today", label: "Today", items: grouped.today },
    { key: "earlier", label: "Earlier", items: grouped.earlier },
  ].filter((section) => section.items.length);
};

const resolveNotificationInitial = (item) => {
  const source = item?.title || item?.message || "N";
  return source.trim().charAt(0).toUpperCase() || "N";
};

const readStoredAdminUser = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem("adminUser");
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    console.error("Failed to parse admin user from storage", error);
    return null;
  }
};

const resolveAdminId = (user) => {
  if (!user) {
    return null;
  }
  const candidateIds = [user.id, user.adminId, user?.admin?.id]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  return candidateIds[0] ?? null;
};

export default function AdminNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [markingAll, setMarkingAll] = useState(false);

  const currentAdminId = useMemo(() => {
    const storedUser = readStoredAdminUser();
    return resolveAdminId(storedUser);
  }, []);

  const refreshNotifications = useCallback(async () => {
    if (!currentAdminId) {
      setError("Unable to resolve admin account.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await client.get("/api/admin/topbar/notifications", {
        params: {
          adminId: currentAdminId,
          limit: 200,
        },
      });
      setNotifications(normalizeNotificationsState(response.data));
    } catch (fetchError) {
      setError(fetchError?.response?.data?.message ?? "Unable to load notifications.");
    } finally {
      setLoading(false);
    }
  }, [currentAdminId]);

  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  const unreadItems = useMemo(
    () => notifications.items.filter((item) => !item.isRead),
    [notifications.items],
  );

  const searchValue = searchTerm.trim().toLowerCase();

  const filteredItems = useMemo(() => {
    const source = filter === "unread" ? unreadItems : notifications.items;
    if (!searchValue) {
      return source;
    }

    return source.filter((item) => {
      const title = String(item.title ?? "").toLowerCase();
      const message = String(item.message ?? "").toLowerCase();
      return title.includes(searchValue) || message.includes(searchValue);
    });
  }, [filter, notifications.items, unreadItems, searchValue]);

  const sections = useMemo(() => groupNotificationsByDate(filteredItems), [filteredItems]);

  const handleNotificationClick = async (item) => {
    if (!item) {
      return;
    }

    const linkUrl = typeof item.linkUrl === "string" && item.linkUrl.trim().length
      ? item.linkUrl
      : "/admin/dashboard";

    if (!item.isRead && currentAdminId) {
      setNotifications((previous) => ({
        ...previous,
        unreadCount: Math.max(0, Number(previous.unreadCount ?? 0) - 1),
        badgeCount: Math.max(0, Number(previous.badgeCount ?? 0) - 1),
        items: previous.items.map((entry) => (
          entry.key === item.key ? { ...entry, isRead: true } : entry
        )),
      }));

      try {
        await client.patch(`/api/admin/topbar/notifications/${encodeURIComponent(item.key)}/read`, {
          adminId: currentAdminId,
        });
      } catch (markReadError) {
        console.error("Unable to mark notification as read", markReadError);
      }
    }

    navigate(linkUrl);
  };

  const handleMarkAllAsRead = async () => {
    if (!currentAdminId || !unreadItems.length || markingAll) {
      return;
    }

    const unreadKeys = unreadItems
      .map((item) => item.key)
      .filter(Boolean);

    setMarkingAll(true);
    setNotifications((previous) => ({
      ...previous,
      unreadCount: 0,
      badgeCount: Math.max(0, Number(previous.badgeCount ?? 0) - unreadItems.length),
      items: previous.items.map((item) => ({ ...item, isRead: true })),
    }));

    try {
      await client.patch("/api/admin/topbar/notifications/read-all", {
        adminId: currentAdminId,
        keys: unreadKeys,
      });
    } catch (markAllError) {
      console.error("Unable to mark all notifications as read", markAllError);
      refreshNotifications();
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <div className="admin-page admin-notifications-page">
      <section className="admin-card admin-notifications-page__card">
        <div className="admin-notifications-page__header">
          <h1>Notifications</h1>
          <div className="admin-notifications-page__controls">
            <div className="admin-notifications-page__tabs" role="tablist" aria-label="Notification filters">
              <button
                type="button"
                className={`admin-notifications-page__tab ${filter === "all" ? "is-active" : ""}`.trim()}
                onClick={() => setFilter("all")}
              >
                All
              </button>
              <button
                type="button"
                className={`admin-notifications-page__tab ${filter === "unread" ? "is-active" : ""}`.trim()}
                onClick={() => setFilter("unread")}
              >
                Unread
              </button>
            </div>

            <label className="admin-notifications-page__search" aria-label="Search notifications">
              <Search size={15} />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search notifications"
              />
            </label>

            <button
              type="button"
              className="admin-button admin-button--ghost"
              onClick={handleMarkAllAsRead}
              disabled={!unreadItems.length || markingAll || loading}
            >
              <CheckCheck size={14} />
              Mark all as read
            </button>
          </div>
        </div>

        <div className="admin-notifications-page__summary">
          {filter === "unread"
            ? `Showing ${filteredItems.length} unread notifications`
            : `Showing ${filteredItems.length} of ${notifications.items.length} notifications`}
        </div>

        {loading ? <div className="admin-notifications-page__status">Loading notifications…</div> : null}
        {!loading && error ? <div className="admin-notifications-page__status is-error">{error}</div> : null}
        {!loading && !error && !filteredItems.length ? (
          <div className="admin-notifications-page__status">No notifications found.</div>
        ) : null}

        {!loading && !error && filteredItems.length ? (
          <div className="admin-notifications-page__sections">
            {sections.map((section) => (
              <section className="admin-notification-section" key={section.key}>
                <div className="admin-notification-section__header">
                  <h5>{section.label}</h5>
                </div>
                <ul className="admin-notification-list is-page">
                  {section.items.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        className={`admin-notification-item is-${item.priority || "info"} ${item.isRead ? "is-read" : ""}`.trim()}
                        onClick={() => handleNotificationClick(item)}
                      >
                        <span className={`admin-notification-item__avatar is-${item.priority || "info"}`.trim()}>
                          {resolveNotificationInitial(item)}
                        </span>
                        <span className="admin-notification-item__content">
                          <span className="admin-notification-item__title">{item.title}</span>
                          <span className="admin-notification-item__message">{item.message}</span>
                          <span className="admin-notification-item__meta">
                            <span className="admin-notification-item__pill">{item.priority || "info"}</span>
                            <span>{item.timeAgo || "just now"}</span>
                          </span>
                        </span>
                        {!item.isRead ? <span className="admin-notification-item__dot" aria-hidden="true" /> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}
